const CART_UPDATE_EVENT = 'cart:update';
const QUANTITY_UPDATE_EVENT = 'quantity-selector:update';
const RECOMMENDATION_LIMIT = 3;
const SHIPPING_RATE_POLL_ATTEMPTS = 12;
const SHIPPING_RATE_POLL_INTERVAL = 500;
const ADD_CONFIRMATION_DELAY = 900;

class LyoCart extends HTMLElement {
  #shippingCents = 0;
  #cartProductIds = [];
  #recommendationRequest = 0;

  connectedCallback() {
    this.#shippingCents = Number(this.dataset.shippingCents) || 0;
    this.#cartProductIds = this.#readProductIds();
    this.shippingForm?.addEventListener('submit', this.#handleShippingSubmit);
    this.recommendations?.addEventListener('click', this.#handleRecommendationClick);
    this.addEventListener('click', this.#handleRemoveClick);
    document.addEventListener(CART_UPDATE_EVENT, this.#handleCartUpdate);
    document.addEventListener(QUANTITY_UPDATE_EVENT, this.#handleQuantityUpdate);
    this.#updateTotals();
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.shippingForm?.removeEventListener('submit', this.#handleShippingSubmit);
    this.recommendations?.removeEventListener('click', this.#handleRecommendationClick);
    this.removeEventListener('click', this.#handleRemoveClick);
    document.removeEventListener(CART_UPDATE_EVENT, this.#handleCartUpdate);
    document.removeEventListener(QUANTITY_UPDATE_EVENT, this.#handleQuantityUpdate);
  }

  get shippingForm() {
    return this.querySelector('[data-lyo-shipping-form]');
  }

  get recommendations() {
    return this.querySelector('[data-lyo-recommendations]');
  }

  #handleShippingSubmit = async (event) => {
    event.preventDefault();
    const form = this.shippingForm;
    if (!(form instanceof HTMLFormElement)) return;

    const postalInput = form.elements.namedItem('postal_code');
    const provinceInput = form.elements.namedItem('province');
    const postalCode = postalInput instanceof HTMLInputElement ? postalInput.value.trim() : '';
    const province = this.#provinceFromPostalCode(postalCode);

    if (!/^\d{5}$/.test(postalCode)) {
      this.#setShippingError('Ingresa un código postal mexicano de 5 dígitos.');
      postalInput?.focus();
      return;
    }
    if (!province) {
      this.#setShippingError('Ingresa un código postal mexicano válido.');
      postalInput?.focus();
      return;
    }
    if (provinceInput instanceof HTMLInputElement) provinceInput.value = province;

    this.#setShippingLoading(true);
    this.#setShippingError('');

    try {
      const parameters = new URLSearchParams({
        'shipping_address[country]': 'MX',
        'shipping_address[zip]': postalCode,
      });
      parameters.set('shipping_address[province]', province);

      const payload = await this.#requestShippingRates(parameters);

      const rates = Array.isArray(payload.shipping_rates) ? payload.shipping_rates : [];
      if (!rates.length) throw new Error('No encontramos opciones de envío para este código postal.');

      const normalizedRates = rates
        .map((rate) => ({ ...rate, cents: this.#priceToCents(rate.price) }))
        .sort((first, second) => first.cents - second.cents);
      this.#shippingCents = normalizedRates[0].cents;
      this.dataset.shippingCents = String(this.#shippingCents);
      this.#renderShippingRates(normalizedRates);
      this.#updateTotals();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo calcular el envío. Intenta de nuevo.';
      console.error('[Lyo shipping calculator]', error);
      this.#setShippingError(message);
    } finally {
      this.#setShippingLoading(false);
    }
  };

  #handleCartUpdate = (event) => {
    if (event.detail?.data?.source === 'lyo-cart') return;
    window.setTimeout(() => this.#refreshCartState(), 0);
  };

  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const line = Number(event.detail?.cartLine);
    const quantity = Number(event.detail?.quantity);
    if (!line || !Number.isFinite(quantity)) return;
    event.stopImmediatePropagation();
    this.#changeLine(line, quantity);
  };

  #handleRemoveClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lyo-remove-line]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    const line = Number(button.dataset.lyoRemoveLine);
    if (!line) return;
    this.#changeLine(line, 0);
  };

  async #changeLine(line, quantity) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;
    this.classList.add('lyo-cart__loading');
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionId,
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload.errors) throw new Error(payload.description || payload.errors || 'No se pudo actualizar la carretilla.');
      const sectionHTML = payload.sections?.[sectionId];
      if (!sectionHTML) throw new Error('No se pudo actualizar esta vista de la carretilla.');
      this.#replaceSection(sectionHTML);
      this.#dispatchCartUpdate(payload);
    } catch (error) {
      const container = this.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
      const message = container?.querySelector(`[ref="cartItemError-${line}"]`);
      if (message) message.textContent = error instanceof Error ? error.message : 'No se pudo actualizar la carretilla.';
      container?.classList.remove('hidden');
    } finally {
      this.classList.remove('lyo-cart__loading');
    }
  }

  #handleRecommendationClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-lyo-add-variant]') : null;
    if (!(target instanceof HTMLButtonElement)) return;

    const variantId = Number(target.dataset.lyoAddVariant);
    if (!variantId) return;

    target.disabled = true;
    target.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.dataset.sectionId,
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || 'No se pudo agregar el producto.');
      target.dataset.added = 'true';
      this.#setRecommendationStatus('Producto agregado al carrito.');
      await new Promise((resolve) => {
        const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 150 : ADD_CONFIRMATION_DELAY;
        window.setTimeout(resolve, delay);
      });
      this.#replaceSection(payload.sections?.[this.dataset.sectionId]);
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : payload;
      this.#dispatchCartUpdate(cart);
    } catch (error) {
      this.#setRecommendationStatus(error instanceof Error ? error.message : 'No se pudo agregar el producto.');
      target.removeAttribute('data-added');
      target.disabled = false;
      target.removeAttribute('aria-busy');
    }
  };

  async #refreshCartState() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.dataset.subtotalCents = String(cart.total_price ?? cart.items_subtotal_price ?? 0);
      this.#cartProductIds = [...new Set((cart.items || []).map((item) => String(item.product_id)))];
      this.dataset.productIds = this.#cartProductIds.join(',');
      this.#updateTotals();
      this.#loadRecommendations();
    } catch (_) {
      // The cart's native components own operation errors; this refresh is best-effort.
    }
  }

  async #loadRecommendations() {
    const container = this.recommendations;
    if (!container || !this.#cartProductIds.length) {
      container?.replaceChildren();
      return;
    }

    const request = ++this.#recommendationRequest;
    const configuredLimit = Number(container.closest('[data-limit]')?.dataset.limit);
    const limit = Number.isFinite(configuredLimit) && configuredLimit > 0
      ? configuredLimit
      : RECOMMENDATION_LIMIT;
    container.setAttribute('aria-busy', 'true');
    this.#setRecommendationStatus('');

    try {
      const excluded = new Set(this.#cartProductIds.map(String));
      const seen = new Set();
      const products = [];
      const appendUniqueProducts = (candidates) => {
        for (const product of candidates.flat()) {
          const id = String(product.id);
          if (!product.available || excluded.has(id) || seen.has(id)) continue;
          seen.add(id);
          products.push(product);
          if (products.length >= limit) break;
        }
      };

      const complementary = await Promise.all(
        this.#cartProductIds.map((productId) => this.#fetchRecommendations(productId, 'complementary'))
      );
      if (request !== this.#recommendationRequest) return;
      appendUniqueProducts(complementary);

      if (products.length < limit) {
        const related = await Promise.all(
          this.#cartProductIds.map((productId) => this.#fetchRecommendations(productId, 'related'))
        );
        if (request !== this.#recommendationRequest) return;
        appendUniqueProducts(related);
      }

      container.replaceChildren(...products.map((product) => this.#createRecommendationCard(product)));
      container.hidden = products.length === 0;
    } catch (_) {
      if (request === this.#recommendationRequest) {
        container.replaceChildren();
        container.hidden = true;
      }
    } finally {
      if (request === this.#recommendationRequest) container.removeAttribute('aria-busy');
    }
  }

  async #fetchRecommendations(productId, intent) {
    const url = new URL('/recommendations/products.json', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  }

  #createRecommendationCard(product) {
    const card = document.createElement('article');
    card.className = 'lyo-cart__recommendation';

    const link = document.createElement('a');
    link.className = 'lyo-cart__recommendation-media';
    link.href = product.url || `/products/${encodeURIComponent(product.handle || '')}`;

    const image = product.featured_image;
    if (image) {
      const img = document.createElement('img');
      img.className = 'lyo-cart__recommendation-image';
      img.src = typeof image === 'string' ? image : image.url || image.src;
      img.alt = typeof image === 'object' && image.alt ? image.alt : product.title || '';
      img.loading = 'lazy';
      img.width = 240;
      img.height = 240;
      link.append(img);
    }

    const title = document.createElement('h3');
    title.className = 'lyo-cart__recommendation-name';
    title.textContent = product.title || '';
    link.append(title);

    const price = document.createElement('span');
    price.className = 'lyo-cart__recommendation-price';
    price.textContent = this.#formatMoney(Number(product.price) || 0);

    const variants = Array.isArray(product.variants) ? product.variants.filter((variant) => variant.available) : [];
    const availableVariant = variants[0];
    let action;
    if (product.variants?.length === 1 && availableVariant) {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'add-to-cart-button lyo-cart__button lyo-cart__button--full lyo-cart__recommendation-add';
      action.dataset.lyoAddVariant = String(availableVariant.id);

      const actionText = document.createElement('span');
      actionText.className = 'add-to-cart-text';
      const iconUrl = this.dataset.cartIconUrl;
      if (iconUrl) {
        const icon = document.createElement('img');
        icon.className = 'lyo-cart__recommendation-add-icon';
        icon.src = iconUrl;
        icon.alt = '';
        icon.width = 20;
        icon.height = 20;
        actionText.append(icon);
      }
      const label = document.createElement('span');
      label.textContent = 'Agregar al carrito';
      actionText.append(label);

      const added = document.createElement('span');
      added.className = 'add-to-cart__added';
      const addedIcon = document.createElement('span');
      addedIcon.className = 'svg-wrapper add-to-cart__added-icon';
      const checkmarkTemplate = this.querySelector('[data-lyo-cart-checkmark]');
      if (checkmarkTemplate instanceof HTMLTemplateElement) {
        addedIcon.append(checkmarkTemplate.content.cloneNode(true));
      }
      added.append(addedIcon);
      action.append(actionText, added);
    } else {
      action = document.createElement('a');
      action.className = 'lyo-cart__button lyo-cart__button--full';
      action.href = link.href;
      action.textContent = 'Elegir opciones';
    }

    card.append(link, price, action);
    return card;
  }

  #renderShippingRates(rates) {
    const selectedRate = rates[0];
    const label = this.querySelector('[data-lyo-shipping-label]');
    if (!selectedRate || !label) return;
    label.textContent = selectedRate.name
      ? `Envío — ${selectedRate.name}`
      : 'Envío';
  }

  #replaceSection(sectionHTML) {
    if (!sectionHTML) throw new Error('No se pudo actualizar esta vista de la carretilla.');
    const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextCart = parsed.querySelector('lyo-cart');
    if (!nextCart) throw new Error('No se pudo reconstruir la carretilla.');

    // Keep the section's stylesheet and module scripts mounted while cart
    // markup changes. Replacing the whole section briefly exposes unstyled
    // product images before the reinserted stylesheet is applied.
    this.replaceWith(document.importNode(nextCart, true));
  }

  #dispatchCartUpdate(cart) {
    document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: { resource: cart, data: { itemCount: cart.item_count, source: 'lyo-cart' } },
    }));
  }

  #updateTotals() {
    const subtotal = Number(this.dataset.subtotalCents) || 0;
    const taxRate = Number(this.dataset.taxRate) || 0;
    const taxCents = Math.round(subtotal * taxRate / (100 + taxRate));
    const total = subtotal + this.#shippingCents;
    this.#setMoney('[data-lyo-shipping-amount]', this.#shippingCents);
    this.#setMoney('[data-lyo-tax-amount]', taxCents);
    this.#setMoney('[data-lyo-estimated-total]', total);
  }

  #setMoney(selector, cents) {
    this.querySelectorAll(selector).forEach((element) => {
      const formattedMoney = this.#formatMoney(cents);
      element.textContent = formattedMoney;
      if (element instanceof HTMLElement && element.tagName === 'TEXT-COMPONENT') {
        element.setAttribute('value', formattedMoney);
      }
    });
  }

  #formatMoney(cents) {
    const locale = 'en-US';
    try {
      const amount = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
      return `$${amount}`;
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #priceToCents(price) {
    const normalized = String(price ?? '').replace(/[^\d.,-]/g, '').replace(',', '.');
    return Math.round((Number.parseFloat(normalized) || 0) * 100);
  }

  #readProductIds() {
    const raw = this.dataset.productIds || '';
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return [...new Set(parsed.map(String).filter(Boolean))];
    } catch (_) {
      // Comma-separated IDs are the compact Liquid-friendly format.
    }
    return [...new Set(raw.split(',').map((id) => id.trim()).filter(Boolean))];
  }

  #provinceFromPostalCode(postalCode) {
    const prefix = Number.parseInt(postalCode.slice(0, 2), 10);
    const ranges = [
      [1, 16, 'Ciudad de México'],
      [20, 20, 'Aguascalientes'],
      [21, 22, 'Baja California'],
      [23, 23, 'Baja California Sur'],
      [24, 24, 'Campeche'],
      [25, 27, 'Coahuila'],
      [28, 28, 'Colima'],
      [29, 30, 'Chiapas'],
      [31, 33, 'Chihuahua'],
      [34, 35, 'Durango'],
      [36, 38, 'Guanajuato'],
      [39, 41, 'Guerrero'],
      [42, 43, 'Hidalgo'],
      [44, 49, 'Jalisco'],
      [50, 57, 'Estado de México'],
      [58, 61, 'Michoacán'],
      [62, 62, 'Morelos'],
      [63, 63, 'Nayarit'],
      [64, 67, 'Nuevo León'],
      [68, 71, 'Oaxaca'],
      [72, 75, 'Puebla'],
      [76, 76, 'Querétaro'],
      [77, 77, 'Quintana Roo'],
      [78, 79, 'San Luis Potosí'],
      [80, 82, 'Sinaloa'],
      [83, 85, 'Sonora'],
      [86, 86, 'Tabasco'],
      [87, 89, 'Tamaulipas'],
      [90, 90, 'Tlaxcala'],
      [91, 96, 'Veracruz'],
      [97, 97, 'Yucatán'],
      [98, 99, 'Zacatecas'],
    ];
    return ranges.find(([start, end]) => prefix >= start && prefix <= end)?.[2] || '';
  }

  #shippingErrorMessage(payload, status) {
    const errors = payload?.errors;
    const errorText = typeof errors === 'string'
      ? errors
      : Array.isArray(errors)
        ? errors.join(' ')
        : Object.values(errors || {}).flat().join(' ');
    const text = errorText
      || payload?.description
      || payload?.message
      || payload?.error;

    if (status === 429) return 'Hay demasiados intentos. Espera un momento y vuelve a intentar.';
    if (text) return String(text);
    if (status === 422) return 'Shopify no pudo generar tarifas para este carrito y destino.';
    if (status === 404) return 'El servicio de cálculo de envío no está disponible en esta ruta.';
    return 'Shopify no devolvió el motivo del error.';
  }

  async #shippingResponsePayload(response) {
    const body = await response.text();
    if (!body) return null;
    try {
      return JSON.parse(body);
    } catch (_) {
      return { message: body };
    }
  }

  #shippingRequestError(stage, response, payload) {
    const requestId = response.headers.get('x-request-id');
    const reason = this.#shippingErrorMessage(payload, response.status);
    const reference = requestId ? ` · Ref. ${requestId}` : '';
    console.error('[Lyo shipping calculator] Shopify request failed', {
      stage,
      status: response.status,
      statusText: response.statusText,
      requestId,
      payload,
    });
    return new Error(`${reason} (HTTP ${response.status}${reference})`);
  }

  async #requestShippingRates(parameters) {
    const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/, '');
    const query = parameters.toString();
    const prepareResponse = await fetch(`${cartUrl}/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });

    if (!prepareResponse.ok) {
      const payload = await this.#shippingResponsePayload(prepareResponse);
      const error = this.#shippingRequestError('prepare_shipping_rates', prepareResponse, payload);
      if (this.#requiresProvince(error.message)) this.#showProvinceField();
      throw error;
    }

    for (let attempt = 0; attempt < SHIPPING_RATE_POLL_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, SHIPPING_RATE_POLL_INTERVAL));
      }

      const response = await fetch(`${cartUrl}/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
      });
      const payload = await this.#shippingResponsePayload(response);

      if (!response.ok) {
        const error = this.#shippingRequestError('async_shipping_rates', response, payload);
        if (this.#requiresProvince(error.message)) this.#showProvinceField();
        throw error;
      }
      if (payload?.shipping_rates) return payload;
    }

    throw new Error('El cálculo del envío está tardando más de lo esperado. Intenta de nuevo.');
  }

  #requiresProvince(message) {
    return /province|state|provincia|estado/i.test(message);
  }

  #showProvinceField() {
    const field = this.querySelector('[data-lyo-province-field]');
    if (!field) return;
    field.hidden = false;
    field.querySelector('input, select')?.focus();
  }

  #setShippingLoading(loading) {
    const button = this.shippingForm?.querySelector('[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = loading;
    this.shippingForm?.setAttribute('aria-busy', String(loading));
  }

  #setShippingError(message) {
    const error = this.querySelector('[data-lyo-shipping-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #setRecommendationStatus(message) {
    const status = this.querySelector('[data-lyo-recommendation-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('lyo-cart')) customElements.define('lyo-cart', LyoCart);
